import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {ActivatedRoute} from "@angular/router";
import {lastValueFrom} from "rxjs";
import {
    PerFormaAPIService,
    ScanProcessDocumentDto,
    ScanProcessDto,
    ScanProcessFieldDto,
    ScanProcessMetadataDto,
    ScanProcessPageDto,
    ScanProcessRegionResponseDto,
    SchemaDto,
    SchemaTypeDto
} from "src/app/services/pfapi.service";
import {NzMessageService} from "ng-zorro-antd/message";
import {orderBy} from "lodash-es";
import {NzNotificationService} from "ng-zorro-antd/notification";
import {format, isValid, parse} from "date-fns";
import {PerformaModulesService} from "src/app/services/performa-modules.service";

interface AggregatedPage {
    Doc: Required<ScanProcessDocumentDto>;
    Page: ScanProcessPageDto;
    PageNumber: number;
    DocumentTypeId: number;
    DocNumber: number;
    Timestamp: number;
    LastDigit?: string;
}

@Component({
    selector: "app-process-scan",
    templateUrl: "./process-scan.component.html",
    styleUrl: "./process-scan.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProcessScanComponent implements OnInit, OnDestroy {
    constructor(
        private readonly api: PerFormaAPIService,
        private readonly msg: NzMessageService,
        private readonly route: ActivatedRoute,
        private readonly notify: NzNotificationService,
        private readonly modules: PerformaModulesService
    ) {}

    @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

    zooming = false;

    // native hover-magnifier state. the legacy jquery elevateZoom plugin is gone — the net10 spa host
    // serves no global jQuery and no /Scripts/* plugin, so the + button arms a cursor-following lens that
    // shows a magnified slice of the same preview image, and the - button disarms it.
    lensVisible = false;
    lensX = 0;
    lensY = 0;
    magnifiedWidth = 0;
    magnifiedHeight = 0;
    magnifiedPosX = 0;
    magnifiedPosY = 0;
    lensSize = 300;
    zoomFactor = 2;

    loading = true;
    savingMetadata = false;
    saving = false;
    rotating = false;
    reprocessing = false;
    deleting = false;
    processing = false;
    reasonModal = false;
    aiEnabled = false;
    reasonText = "";

    scan: ScanProcessDto;
    activeRegion?: ScanProcessRegionResponseDto;
    docTypes: SchemaTypeDto[];
    selectedPage?: AggregatedPage;
    autofillFrame?: HTMLIFrameElement;
    selectedMetadata?: ScanProcessMetadataDto;
    schema: SchemaDto;
    pages?: AggregatedPage[];

    // object url for the authenticated large-image fetch of the selected page. the scan-image endpoint
    // requires a bearer token, so a raw <img src>/background-image can't load it — we fetch the blob and
    // expose it as an object url for the preview background, the hidden zoom <img>, and the full-screen link.
    currentPageUrl?: string;
    currentPageBg = "";
    private currentPageRequestId = 0;

    async ngOnInit() {
        this.aiEnabled = await this.modules.AiEnabled();
        this.loadFrame();
        window.scrollTo({top: 0});
        const id = this.route.snapshot.queryParamMap.get("id");
        if (!id) {
            window.location.href = "/Scan";
        }
        try {
            this.schema = await lastValueFrom(this.api.getApiSystemSchema());
            this.docTypes = this.schema?.Types;
            this.pages = await this.loadPages(id);
            this.selectedPage = this.pages.find(
                x => x.Page.ScanPageId == (this.scan.SelectedPage || this.pages[0]?.Page?.ScanPageId)
            );
            await this.loadMetadata(this.selectedPage);
            await this.loadCurrentPage();
            setTimeout(() => {
                this.scrollToSelection();
            }, 0);
        } finally {
            this.loading = false;
        }
    }

    scrollToSelection = () => {
        if (!this.viewport || !this.pages?.length || !this.selectedPage) return;
        const index = this.pages.findIndex(p => p.Page.ScanPageId === this.selectedPage.Page.ScanPageId);
        if (index >= 0) {
            this.viewport.scrollToIndex(index, "smooth");
        }
    };

    trackByPageId = (_index: number, pg: AggregatedPage) => pg.Page.ScanPageId;

    // fetches the large image for the selected page through the authenticated client and exposes it as
    // an object url. the Timestamp is passed as a cache-bust so a rotated/edited page re-fetches rather
    // than serving a stale cached blob. the previous url is revoked once the new one is ready.
    loadCurrentPage = async () => {
        const pg = this.selectedPage;
        const requested = ++this.currentPageRequestId;
        if (!pg) {
            this.revokeCurrentPageUrl();
            this.currentPageBg = "";
            return;
        }
        try {
            const blob = await lastValueFrom(
                this.api.getApiScanImage(
                    "image/jpeg",
                    {scanPageId: pg.Page.ScanPageId, large: true},
                    {params: {_ts: pg.Timestamp}}
                )
            );
            // a newer selection superseded this fetch while it was in flight — drop the stale blob.
            if (requested !== this.currentPageRequestId) return;
            this.revokeCurrentPageUrl();
            this.currentPageUrl = URL.createObjectURL(blob);
            this.currentPageBg = `url("${this.currentPageUrl}")`;
        } catch {
            // a missing large image (404) leaves the preview blank — non-fatal.
        }
    };

    private revokeCurrentPageUrl(): void {
        if (this.currentPageUrl) {
            URL.revokeObjectURL(this.currentPageUrl);
            this.currentPageUrl = undefined;
        }
    }

    ngOnDestroy() {
        this.autofillFrame?.remove();
        this.autofillFrame = null;
        this.revokeCurrentPageUrl();
    }

    loadPages = async id => {
        const ts = new Date().getTime();
        this.scan = await lastValueFrom(this.api.getApiScanProcessSummary({scanId: Number(id)}));
        const pages = this.scan.Documents;
        return orderBy(
            pages
                .flatMap(x => x.Pages.map(y => ({Doc: x, Page: y})))
                .map(
                    x =>
                        ({
                            Page: x.Page,
                            PageNumber: x.Page.PageNumber,
                            DocNumber: x.Doc.DocumentNumber,
                            DocumentTypeId: x.Doc.DocumentTypeId,
                            Doc: x.Doc,
                            LastDigit: x.Doc.DocumentNumber.toString().charAt(
                                x.Doc.DocumentNumber.toString().length - 1
                            ),
                            Timestamp: ts
                        }) as AggregatedPage
                ),
            ["DocNumber", "PageNumber"],
            ["asc", "asc"]
        ) as AggregatedPage[];
    };

    savePage = async () => {
        try {
            this.saving = true;
            await lastValueFrom(
                this.api.postApiScanProcessUpdatePage({
                    ScanId: this.scan.ScanId,
                    CurrentDocumentNumber: this.selectedPage.Doc.DocumentNumber,
                    CurrentDocumentTypeId: this.selectedPage.Doc.DocumentTypeId,
                    CurrentPageNumber: this.selectedPage.Page.PageNumber,
                    NewDocumentNumber: this.selectedPage.DocNumber,
                    NewDocumentTypeId: this.selectedPage.DocumentTypeId,
                    NewPageNumber: this.selectedPage.PageNumber
                })
            );
            this.loading = true;
            const reload = this.selectedPage.Doc.DocumentNumber !== this.selectedPage.DocNumber;
            this.pages = await this.loadPages(this.scan.ScanId);
            this.selectedPage = this.pages.find(
                x => x.Page.ScanPageId == (this.scan.SelectedPage || this.pages[0]?.Page?.ScanPageId)
            );
            if (reload) {
                await this.loadMetadata(this.selectedPage);
            }
            await this.loadCurrentPage();
            this.msg.success("Page saved successfully");
        } finally {
            this.saving = false;
            this.loading = false;
        }
    };

    deletePage = async () => {
        try {
            this.deleting = true;
            await lastValueFrom(
                this.api.deleteApiScanProcessDeletePage({
                    ScanId: this.scan.ScanId,
                    PageId: this.selectedPage.Page.ScanPageId
                })
            );
            this.loading = true;
            this.pages = await this.loadPages(this.scan.ScanId);
            this.selectedPage = this.pages.find(
                x => x.Page.ScanPageId == (this.scan.SelectedPage || this.pages[0]?.Page?.ScanPageId)
            );
            await this.loadCurrentPage();
            this.msg.success("Page deleted successfully");
        } finally {
            this.deleting = false;
            this.loading = false;
        }
    };

    // arm the magnifier — the lens itself appears on the first mousemove over the preview.
    zoomIn = () => {
        this.zooming = true;
    };

    zoomOut = () => {
        this.zooming = false;
        this.lensVisible = false;
    };

    // track the cursor over the preview and position the lens + its magnified background so the pixel
    // under the cursor sits at the lens centre. the preview fills #currentPageContainer (the hidden img
    // sizes it, the image is painted as the container background), so the container rect is the image rect.
    onMagnifierMove = (event: MouseEvent) => {
        if (!this.zooming) return;
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
            this.lensVisible = false;
            return;
        }
        const half = this.lensSize / 2;
        this.lensVisible = true;
        this.lensX = x - half;
        this.lensY = y - half;
        this.magnifiedWidth = rect.width * this.zoomFactor;
        this.magnifiedHeight = rect.height * this.zoomFactor;
        this.magnifiedPosX = half - x * this.zoomFactor;
        this.magnifiedPosY = half - y * this.zoomFactor;
    };

    onMagnifierLeave = () => {
        this.lensVisible = false;
    };

    selectPage = async (pg: AggregatedPage, event?: Event) => {
        // the thumbnail is an <a href="#"> for styling; stop the browser following the anchor.
        event?.preventDefault();
        try {
            this.loading = true;
            await lastValueFrom(
                this.api.putApiScanProcessSelect({
                    PageId: pg.Page.ScanPageId,
                    ScanId: this.scan.ScanId
                })
            );
            const newPage = this.pages.find(x => x.Page.ScanPageId == pg.Page.ScanPageId);
            if (this.selectedPage.Doc.ScanDocumentId !== newPage.Doc.ScanDocumentId) {
                await this.loadMetadata(newPage);
            }
            this.selectedPage = newPage;
            await this.loadCurrentPage();
        } finally {
            this.loading = false;
        }
    };

    loadMetadata = async (pg: AggregatedPage) => {
        const meta = await lastValueFrom(this.api.getApiScanProcessMetadata({scanDocumentId: pg.Doc.ScanDocumentId}));
        if (meta?.Fields) {
            meta.Fields.forEach(f => {
                try {
                    if (f.TypeId === 3 && f.Value) {
                        const parsed = parse(f.Value, f.DateFormat ?? "", new Date()) as unknown as string;
                        if (!isValid(parsed)) {
                            f.Value = "";
                        }
                    }
                } catch (e) {
                    console.error("Failed to parse field", f, e);
                    f.Value = null;
                }
            });
        }
        this.selectedMetadata = meta;
    };

    rotatePage = async (angle: number) => {
        try {
            this.rotating = true;
            this.loading = true;
            await lastValueFrom(
                this.api.postApiScanProcessRotate({
                    Angle: angle,
                    ScanPageId: this.selectedPage.Page.ScanPageId
                })
            );
            this.selectedPage.Timestamp = new Date().getTime();
            await this.loadCurrentPage();
        } finally {
            this.rotating = false;
            this.loading = false;
        }
    };

    saveMetadata = async () => {
        try {
            this.savingMetadata = true;
            const vals = Object.fromEntries(
                (this.selectedMetadata?.Fields ?? []).map(x => [
                    x.MetadataId,
                    x.TypeId === 3
                        ? !x.Value
                            ? null
                            : format(x.Value as unknown as Date, x.DateFormat ?? "")
                        : x.Value || null
                ])
            );
            await lastValueFrom(
                this.api.putApiScanProcessMetadata({
                    ScanDocumentId: this.selectedMetadata?.ScanDocumentId,
                    Values: vals as {[key: string]: string}
                })
            );
            this.selectedPage.Timestamp = new Date().getTime();
            this.triggerAutofillSave();
            this.msg.success("Metadata saved");
        } catch (e) {
            if (e?.error?.Message) {
                this.notify.error("Unable to save", e?.error?.Message, {
                    nzDuration: 5000,
                    nzPlacement: "top",
                    nzStyle: {width: "400px"},
                    nzCloseIcon: undefined
                });
            }
        } finally {
            this.savingMetadata = false;
        }
    };

    finalize = async () => {
        try {
            this.processing = true;
            await lastValueFrom(
                this.api.postApiScanProcessFinalize({
                    ScanId: this.scan.ScanId
                })
            );
            window.location.href = "/Scan";
        } catch (e) {
            if (e?.error?.Message) {
                this.notify.error("Unable to process", e?.error?.Message, {
                    nzDuration: 5000,
                    nzPlacement: "top",
                    nzStyle: {width: "400px"},
                    nzCloseIcon: undefined
                });
            }
        } finally {
            this.processing = false;
        }
    };

    reprocessDocument = async (reason?) => {
        try {
            this.reprocessing = true;
            await lastValueFrom(
                this.api.postApiScanProcessReprocess({
                    ScanId: this.scan.ScanId,
                    ...(reason ? {Reason: reason} : {})
                })
            );
            window.location.href = "/Scan";
        } finally {
            this.reprocessing = false;
        }
    };

    triggerAutofillSave = () => {
        const form = document.getElementById("metaForm") as HTMLFormElement;
        form.target = "autofill-target";
        form.action = "about:blank";
        form.method = "POST";
        form.submit();
    };

    getConfidenceColor(confidence: number): string {
        const level = confidence * 100;
        if (level < 65) return "rgb(255, 166, 166)";
        if (level < 80) return "rgb(245, 255, 166)";
        return "";
    }

    getConfidenceTooltip(confidence: number): string {
        return "Confidence: " + Math.round(confidence * 100) + "%";
    }

    onFieldFocus = async (field: ScanProcessFieldDto) => {
        if (!this.selectedMetadata?.Template?.Id) return;
        await this.loadRegion(this.selectedMetadata.Template.Id, field.MetadataId);
    };

    onFieldBlur(): void {
        this.activeRegion = null;
    }

    loadRegion = async (templateId: number, metadataId: number) => {
        const resp = await lastValueFrom(
            this.api.postApiScanProcessRegion({ZonalTemplateId: templateId, MetadataId: metadataId})
        );
        this.activeRegion = resp;
    };

    loadFrame = () => {
        this.autofillFrame = document.createElement("iframe");
        this.autofillFrame.name = "autofill-target";
        this.autofillFrame.style.display = "none";
        document.body.appendChild(this.autofillFrame);
    };
}
